import { sizer } from "layout/styles/styled/sizer";
import { Label } from "layout/typography/Label";
import { getPaymentPlanEnabled } from "pages/CreateOrder/utils/helpers";
import { Card } from "primereact/card";
import { SelectButton } from "primereact/selectbutton";
import { useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import styled from "styled-components";
import { IAccountResponse } from "types/Account/AccountResponse";
import { IPaymentPlan } from "types/Payment";

import { DownpaymentOnlySection } from "./components/DownpaymentOnlySection";
import { PaymentPlanSection } from "./components/PaymentPlanSection";

interface IPaymentPlanProps {
  accountData: IAccountResponse;
  paymentPlan?: IPaymentPlan;
  isRedo?: boolean;
  isDraft?: boolean;
  isCopy?: boolean;
}

const StyledCard = styled(Card)`
  margin-bottom: ${sizer(4)};
  .p-card-subtitle {
    color: var(--orange-500);
  }
`;

const StyledSelectButton = styled(SelectButton)`
  .p-button {
    margin-right: 8px;
    border-radius: 6px !important;
    padding: 8px 20px;
    margin-bottom: 8px;
  }
  .p-button:last-child {
    margin-right: 0;
  }
  .p-button:not(.p-highlight) {
    color: var(--gray-600);
    background-color: var(--gray-50);
    border: 1px solid var(--gray-300);
  }
`;

export function PaymentPlan({
  accountData,
  paymentPlan,
  isRedo,
  isDraft,
  isCopy,
}: IPaymentPlanProps) {
  const { watch, setValue, trigger } = useFormContext();
  const isDownPaymentOnly = watch("isDownPaymentOnly");
  const orderTypeField = watch("orderType");

  const selectedOption = isDownPaymentOnly ? "downPaymentOnly" : "paymentPlan";

  const paymentPlanIsEnabledForOrderType = useMemo(
    () =>
      getPaymentPlanEnabled(
        orderTypeField,
        accountData.data.paymentPlanTypeEnabled
      ),
    [orderTypeField, accountData.data.paymentPlanTypeEnabled]
  );

  const { partialPaymentsEnabled, providerDistributionLogic } =
    accountData.data;

  const availableOptions = useMemo(() => {
    const options = [];
    if (paymentPlan) {
      if (paymentPlan.isDownPaymentOnly) {
        if (paymentPlanIsEnabledForOrderType) {
          options.push({ label: "Payment Plan", value: "paymentPlan" });
        }
      } else {
        options.push({ label: "Payment Plan", value: "paymentPlan" });
      }

      if (partialPaymentsEnabled) {
        options.push({ label: "Downpayment Only", value: "downPaymentOnly" });
      }
      return options;
    }
    if (paymentPlanIsEnabledForOrderType) {
      options.push({ label: "Payment Plan", value: "paymentPlan" });
    }
    if (partialPaymentsEnabled) {
      options.push({ label: "Downpayment Only", value: "downPaymentOnly" });
    }
    return options;
  }, [paymentPlanIsEnabledForOrderType, partialPaymentsEnabled]);

  const handlePaymentOptionChange = (e: any) => {
    if (e.value === "downPaymentOnly") {
      setValue("isDownPaymentOnly", true);
      setValue("providerDistributionLogic", providerDistributionLogic);
      trigger("downpaymentOnlyPercent");
      trigger("downpaymentOnlyFlatAmount");
    } else {
      setValue("isDownPaymentOnly", false);
      setValue("downpaymentOnlyType", "Percentage");
      setValue("downpaymentOnlyPercent", null);
      setValue("downpaymentOnlyFlatAmount", null);
      // clear validation errors
      trigger("downpaymentOnlyPercent");
      trigger("downpaymentOnlyFlatAmount");
    }
  };

  // Auto-set isPartialPayment based on what's available

  useEffect(() => {
    if (availableOptions.length === 0) {
      setValue("isDownPaymentOnly", false);
      setValue("paymentPlanEnabled", false);
    } else if (availableOptions.length === 1) {
      setValue(
        "isDownPaymentOnly",
        availableOptions[0].value === "downPaymentOnly"
      );
    }
  }, [availableOptions]);

  if (!availableOptions.length) {
    return null;
  }

  return (
    <StyledCard data-testid="paymentPlan_Card" title="Payment Options">
      {/* Only show select button if both options are available */}
      {availableOptions.length > 1 && (
        <StyledSelectButton
          id="paymentOption"
          data-testid="paymentOption_SelectButton"
          value={selectedOption}
          options={availableOptions}
          onChange={(e) => e.value && handlePaymentOptionChange(e)}
          optionLabel="label"
          optionValue="value"
        />
      )}
      {!isDownPaymentOnly && (
        <PaymentPlanSection
          accountData={accountData}
          paymentPlan={paymentPlan}
          isRedo={isRedo}
          isDraft={isDraft}
        />
      )}
      {isDownPaymentOnly && (
        <DownpaymentOnlySection
          downpaymentOnlyPaymentPlan={
            paymentPlan?.isDownPaymentOnly ? paymentPlan : undefined
          }
          isRedo={isRedo}
          isDraft={isDraft}
          isCopy={isCopy}
        />
      )}
    </StyledCard>
  );
}
